import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { FilterTabs } from './ui/FilterTabs';
import { FilterSection } from './ui/FilterSection';
import { AlertMessage } from './ui/AlertMessage';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { Modal } from './ui/Modal';
import { formControlClass, formLabelClass } from './ui/fieldStyles';
import { AuthenticatedImage } from './ui/AuthenticatedImage';
import { getAuthHeaders, API_BASE_URL } from '../api/mediaUrl';

const initialForm = {
  name: '',
  email: '',
  mobile: '',
  roleId: '',
  password: '',
  confirmPassword: '',
  profileImage: '',
};

const normalizeApiMessage = (message, fallback) => {
  const text = (message || fallback || '').trim();
  if (text === 'size must be between 8 and 100') {
    return 'Password must be between 8 and 100 characters';
  }
  return text || fallback;
};

const getApiErrorMessage = (errData, fallback) => {
  if (!errData) {
    return fallback;
  }
  if (typeof errData.error === 'string' && errData.error.trim()) {
    return normalizeApiMessage(errData.error, fallback);
  }
  if (typeof errData.message === 'string' && errData.message.trim()) {
    return normalizeApiMessage(errData.message, fallback);
  }
  if (typeof errData === 'object') {
    const firstFieldMessage = Object.values(errData).find(
      (value) => typeof value === 'string' && value.trim()
    );
    if (firstFieldMessage) {
      return normalizeApiMessage(firstFieldMessage, fallback);
    }
  }
  return normalizeApiMessage('', fallback);
};

const getPasswordValidationMessage = (value) => {
  if (!value || !value.trim()) {
    return 'Please enter password';
  }
  if (value.length < 8 || value.length > 100) {
    return 'Password must be between 8 and 100 characters';
  }
  if (!/[a-z]/.test(value)) {
    return 'Password must include at least one lowercase letter';
  }
  if (!/[A-Z]/.test(value)) {
    return 'Password must include at least one uppercase letter';
  }
  if (!/[0-9]/.test(value)) {
    return 'Password must include at least one number';
  }
  if (!/[@#$%^&+=!]/.test(value)) {
    return 'Password must include at least one special character (@#$%^&+=!)';
  }
  return '';
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState('');
  /** 'all' | 'active' | 'inactive' */
  const [userFilter, setUserFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [pageMessage, setPageMessage] = useState('');
  const [pageError, setPageError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordForm, setPasswordForm] = useState({ password: '', confirmPassword: '' });
  const [selectedPasswordUser, setSelectedPasswordUser] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [confirmStatusUser, setConfirmStatusUser] = useState(null);

  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const headers = useMemo(() => {
    const h = { 'Content-Type': 'application/json' };
    if (token) {
      h.Authorization = `Bearer ${token}`;
    }
    return h;
  }, [token]);

  useEffect(() => {
    fetchUsersAndRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pageMessage) {
      return undefined;
    }
    const timer = setTimeout(() => setPageMessage(''), 3500);
    return () => clearTimeout(timer);
  }, [pageMessage]);

  useEffect(() => {
    if (!pageError) {
      return undefined;
    }
    const timer = setTimeout(() => setPageError(''), 5000);
    return () => clearTimeout(timer);
  }, [pageError]);

  useEffect(() => {
    if (!formError) {
      return undefined;
    }
    const timer = setTimeout(() => setFormError(''), 5000);
    return () => clearTimeout(timer);
  }, [formError]);

  useEffect(() => {
    if (!passwordError) {
      return undefined;
    }
    const timer = setTimeout(() => setPasswordError(''), 5000);
    return () => clearTimeout(timer);
  }, [passwordError]);

  const fetchUsersAndRoles = async () => {
    setLoading(true);
    setPageError('');
    try {
      const [usersRes, rolesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin-users`, { headers }),
        fetch(`${API_BASE_URL}/api/roles/all`, { headers }),
      ]);

      if (!usersRes.ok || !rolesRes.ok) {
        throw new Error('Failed to load admin users');
      }

      const usersData = await usersRes.json();
      const rolesData = await rolesRes.json();
      setUsers(Array.isArray(usersData) ? usersData : []);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (err) {
      setPageError(err.message || 'Unable to fetch admin users');
      setUsers([]);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const userTabCounts = useMemo(() => {
    let all = 0;
    let active = 0;
    let inactive = 0;
    for (const u of users) {
      all += 1;
      if (u.active) active += 1;
      else inactive += 1;
    }
    return { all, active, inactive };
  }, [users]);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      if (userFilter === 'active' && !user.active) return false;
      if (userFilter === 'inactive' && user.active) return false;
      const key = search.trim().toLowerCase();
      if (!key) {
        return true;
      }
      return (
        (user.name || '').toLowerCase().includes(key) ||
        (user.email || '').toLowerCase().includes(key) ||
        (user.mobile || '').toLowerCase().includes(key) ||
        (user.role || '').toLowerCase().includes(key)
      );
    });
  }, [users, search, userFilter]);

  const onImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setUploadingImage(true);
    setFormError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      let uploadUrl = `${API_BASE_URL}/api/files/upload/profile`;
      if (formMode === 'edit' && editingUser?.id) {
        uploadUrl += `?userId=${encodeURIComponent(editingUser.id)}`;
      } else if (form.name?.trim()) {
        uploadUrl += `?nameHint=${encodeURIComponent(form.name.trim())}`;
      }
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errData, 'Unable to upload profile image'));
      }
      const uploaded = await response.json();
      setForm((prev) => ({ ...prev, profileImage: uploaded.key || '' }));
    } catch (err) {
      setFormError(err.message || 'Unable to upload profile image');
    } finally {
      setUploadingImage(false);
    }
  };

  const updateField = (field, value) => {
    setFieldErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      return { ...prev, [field]: '' };
    });
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePasswordInvalid = (e) => {
    e.target.setCustomValidity(getPasswordValidationMessage(e.target.value));
  };

  const handlePasswordInput = (e) => {
    e.target.setCustomValidity('');
  };

  const handleConfirmPasswordInvalid = (e) => {
    const value = e.target.value || '';
    if (!value.trim()) {
      e.target.setCustomValidity('Please confirm password');
      return;
    }
    const basePassword = e.target.name === 'resetConfirmPassword'
      ? passwordForm.password
      : form.password;
    if (value !== basePassword) {
      e.target.setCustomValidity('Confirm password must match password');
      return;
    }
    e.target.setCustomValidity('');
  };

  const handleConfirmPasswordInput = (e) => {
    e.target.setCustomValidity('');
  };

  const closeForm = () => {
    setShowForm(false);
    setFormMode('create');
    setEditingUser(null);
    setForm(initialForm);
    setSaving(false);
    setFormError('');
    setFieldErrors({});
  };

  const openCreateForm = () => {
    setFormMode('create');
    setEditingUser(null);
    setForm(initialForm);
    setFormError('');
    setFieldErrors({});
    setShowForm(true);
  };

  const openEditForm = (user) => {
    if (user.systemRole === 'OWNER_ADMIN') {
      setPageError('OWNER_ADMIN account cannot be edited from this page');
      return;
    }
    setFormMode('edit');
    setEditingUser(user);
    setForm({
      name: user.name || '',
      email: user.email || '',
      mobile: user.mobile || '',
      roleId: roles.find((r) => r.name === user.role)?.id ?? '',
      password: '',
      confirmPassword: '',
      profileImage: user.profileImage || '',
    });
    setFormError('');
    setFieldErrors({});
    setShowForm(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setPageError('');
    setPageMessage('');
    setFormError('');
    setFieldErrors({});

    const name = form.name.trim();
    const email = form.email.trim();
    const mobile = form.mobile.trim();
    const roleId = form.roleId;

    if (!name || !email || !mobile || !String(roleId).trim()) {
      const nextFieldErrors = {};
      if (!name) nextFieldErrors.name = 'Please enter name';
      if (!email) nextFieldErrors.email = 'Please enter email';
      if (!mobile) nextFieldErrors.mobile = 'Please enter mobile number';
      if (!String(roleId).trim()) nextFieldErrors.roleId = 'Please select role';
      setFieldErrors(nextFieldErrors);
      return;
    }
    if (!isValidEmail(email)) {
      setFieldErrors({ email: 'Please enter a valid email address' });
      return;
    }
    if (!/^[0-9]{10,15}$/.test(mobile)) {
      setFieldErrors({ mobile: 'Mobile must be 10 to 15 digits' });
      return;
    }
    if (formMode === 'create') {
      if (!form.password || !form.confirmPassword) {
        const nextFieldErrors = {};
        if (!form.password) nextFieldErrors.password = 'Please enter password';
        if (!form.confirmPassword) nextFieldErrors.confirmPassword = 'Please confirm password';
        setFieldErrors(nextFieldErrors);
        return;
      }
      const passwordValidationMessage = getPasswordValidationMessage(form.password);
      if (passwordValidationMessage) {
        setFieldErrors({ password: passwordValidationMessage });
        return;
      }
      if (form.password !== form.confirmPassword) {
        setFieldErrors({ confirmPassword: 'Confirm password must match password' });
        return;
      }
    }

    setSaving(true);
    try {
      const url =
        formMode === 'create'
          ? `${API_BASE_URL}/api/admin-users`
          : `${API_BASE_URL}/api/admin-users/${editingUser.id}`;
      const method = formMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({
          name,
          email,
          mobile,
          roleId,
          ...(formMode === 'create'
            ? { password: form.password, confirmPassword: form.confirmPassword }
            : {}),
          profileImage: form.profileImage || null,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const apiMessage = getApiErrorMessage(
          errData,
          formMode === 'create' ? 'Failed to create user' : 'Failed to update user'
        );
        const lower = apiMessage.toLowerCase();
        if (lower.includes('email') && lower.includes('exists')) {
          setFieldErrors({ email: apiMessage });
          return;
        }
        if (lower.includes('mobile') && (lower.includes('exists') || lower.includes('10 to 15 digits'))) {
          setFieldErrors({ mobile: apiMessage });
          return;
        }
        if (lower.includes('password') && lower.includes('confirm') && lower.includes('match')) {
          setFieldErrors({ confirmPassword: apiMessage });
          return;
        }
        if (lower.includes('role')) {
          setFieldErrors({ roleId: apiMessage });
          return;
        }
        throw new Error(apiMessage);
      }

      const savedUser = await response.json();
      if (formMode === 'create') {
        setUsers((prev) => [savedUser, ...prev]);
        setPageMessage('User created successfully');
      } else {
        setUsers((prev) => prev.map((u) => (u.id === savedUser.id ? savedUser : u)));
        setPageMessage('User updated successfully');
      }
      closeForm();
    } catch (err) {
      setFormError(err.message || 'Unable to save user');
    } finally {
      setSaving(false);
    }
  };

  const openPasswordModal = (user) => {
    if (user.systemRole === 'OWNER_ADMIN') {
      setPageError('OWNER_ADMIN password cannot be updated from this page');
      return;
    }
    setSelectedPasswordUser(user);
    setPasswordForm({ password: '', confirmPassword: '' });
    setPasswordError('');
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedPasswordUser(null);
    setPasswordForm({ password: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordLoading(false);
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!selectedPasswordUser) {
      return;
    }

    setPasswordError('');
    if (!passwordForm.password || !passwordForm.confirmPassword) {
      setPasswordError('Please fill both password fields');
      return;
    }
    if (passwordForm.password !== passwordForm.confirmPassword) {
      const confirmInput = e.currentTarget.querySelector('input[name="resetConfirmPassword"]');
      if (confirmInput) {
        confirmInput.setCustomValidity('Confirm password must match password');
        confirmInput.reportValidity();
      }
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-users/${selectedPasswordUser.id}/password`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          password: passwordForm.password,
          confirmPassword: passwordForm.confirmPassword,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errData, 'Failed to update password'));
      }

      setPageMessage('Password updated successfully');
      closePasswordModal();
    } catch (err) {
      setPasswordError(err.message || 'Unable to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const toggleUserStatus = async (user) => {
    const action = user.active ? 'deactivate' : 'activate';
    setConfirmStatusUser(null);
    setStatusLoadingId(user.id);
    setPageError('');
    setPageMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-users/${user.id}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ active: !user.active }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errData, `Failed to ${action} user`));
      }

      const updated = await response.json();
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setPageMessage(`User ${user.active ? 'deactivated' : 'activated'} successfully`);
    } catch (err) {
      setPageError(err.message || `Unable to ${action} user`);
    } finally {
      setStatusLoadingId(null);
    }
  };

  return (
    <div>
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar
            tone="light"
            title="Admin / Staff User"
            actions={
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={openCreateForm}
                className="h-11 gap-2 whitespace-nowrap rounded-xl px-5"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add User</span>
              </Button>
            }
          />
        </CardHeader>
        <CardBody>
          <AlertMessage
            message={pageMessage}
            type="success"
            onClose={() => setPageMessage('')}
            className="mb-4"
          />
          <AlertMessage
            message={pageError}
            type="error"
            onClose={() => setPageError('')}
            className="mb-4"
          />
          {!loading && users.length > 0 && (
            <FilterSection className="mb-4" hint="Filter by account status, then search by name, email, or mobile.">
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-600">Status</p>
                <FilterTabs
                  value={userFilter}
                  onChange={setUserFilter}
                  size="sm"
                  tabs={[
                    { id: 'all', label: 'All', count: userTabCounts.all },
                    { id: 'active', label: 'Active', count: userTabCounts.active },
                    { id: 'inactive', label: 'Inactive', count: userTabCounts.inactive },
                  ]}
                />
              </div>
              <Input
                type="search"
                label="Search users"
                placeholder="Name, email, mobile, or role"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </FilterSection>
          )}

          {loading ? (
            <div className="py-8 text-center text-gray-500">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No users found</div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="w-full bg-white">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">#</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Image</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Mobile</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">System Role</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr key={user.id} className="border-b last:border-b-0 border-gray-100">
                      <td className="py-3 px-4 text-sm text-gray-500">{index + 1}</td>
                      <td className="py-3 px-4">
                        {user.profileImage ? (
                          <AuthenticatedImage src={user.profileImage} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600">
                            {(user.name || 'U').charAt(0).toUpperCase()}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-800">{user.name}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{user.mobile || '-'}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{user.email}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{user.role || '-'}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          user.systemRole === 'OWNER_ADMIN'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {user.systemRole || 'USER'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          user.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                        }`}>
                          {user.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {user.systemRole === 'OWNER_ADMIN' ? (
                          <span className="text-xs text-gray-500 font-medium">Locked</span>
                        ) : (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEditForm(user)}
                            className="px-2.5 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => openPasswordModal(user)}
                            className="px-2.5 py-1.5 text-xs rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100"
                          >
                            Reset Password
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmStatusUser(user)}
                            disabled={statusLoadingId === user.id || currentUser.email === user.email}
                            className={`px-2.5 py-1.5 text-xs rounded-lg ${
                              user.active
                                ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                : 'bg-green-50 text-green-700 hover:bg-green-100'
                            } disabled:opacity-50`}
                          >
                            {statusLoadingId === user.id
                              ? 'Updating...'
                              : user.active
                                ? 'Deactivate'
                                : 'Activate'}
                          </button>
                        </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={showForm}
        onClose={closeForm}
        title={formMode === 'create' ? 'Add admin user' : 'Edit admin user'}
        description="Fill user details and account access information."
        size="xl"
        icon={(
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        )}
        bodyClassName="bg-slate-50/50"
        footer={(
          <>
            <Button type="button" variant="secondary" onClick={closeForm} disabled={saving}>Cancel</Button>
            <Button type="submit" form="admin-user-form" variant="primary" loading={saving} disabled={saving}>
              {formMode === 'create' ? 'Create user' : 'Update user'}
            </Button>
          </>
        )}
      >
        {showForm && (
          <form noValidate onSubmit={handleSaveUser} id="admin-user-form" className="py-5 sm:py-6 space-y-5">
              {formError && (
                <AlertMessage
                  message={formError}
                  type="error"
                  onClose={() => setFormError('')}
                  className="mb-2"
                />
              )}

              <div className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4">
                <div className="flex items-center justify-center">
                <label className="cursor-pointer group">
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onImageChange} />
                  {form.profileImage ? (
                    <AuthenticatedImage src={form.profileImage} alt="Profile" className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md ring-2 ring-primary-100 group-hover:ring-primary-200 transition-shadow" />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center text-slate-500 border-2 border-dashed border-slate-200 group-hover:border-primary-300 group-hover:text-primary-700 transition-colors text-sm font-medium">
                      Photo
                    </div>
                  )}
                </label>
                </div>
                <p className="text-center text-xs text-slate-500 mt-2">
                  Upload profile image (optional){uploadingImage ? ' — uploading…' : ''}
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200/80 bg-white p-4 md:p-5 space-y-4 shadow-sm">
                <h4 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Name *"
                    value={form.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    error={fieldErrors.name}
                    required
                  />
                  <Input
                    label="Email *"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    error={fieldErrors.email}
                    required
                  />
                <Input
                  label="Mobile *"
                  value={form.mobile}
                  onChange={(e) => updateField('mobile', e.target.value.replace(/\D/g, ''))}
                  error={fieldErrors.mobile}
                  required
                />
                  <div>
                    <label className={formLabelClass}>Role *</label>
                    <select
                      value={form.roleId}
                      onChange={(e) => updateField('roleId', e.target.value)}
                      className={formControlClass({ error: Boolean(fieldErrors.roleId) })}
                      required
                    >
                      <option value="">Select role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={String(role.id)}>{role.name}</option>
                      ))}
                    </select>
                    {fieldErrors.roleId && <p className="mt-1.5 text-sm text-rose-600">{fieldErrors.roleId}</p>}
                  </div>
                </div>
              </div>

              {formMode === 'create' && (
                <div className="rounded-2xl border border-slate-200/80 bg-white p-4 md:p-5 space-y-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Security</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formMode === 'create' && (
                  <Input
                    label="Password *"
                    type="password"
                    value={form.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    minLength={8}
                    maxLength={100}
                    pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+=!]).{8,100}$"
                    title="Password must contain uppercase, lowercase, number and special character"
                    onInvalid={handlePasswordInvalid}
                    onInput={handlePasswordInput}
                    error={fieldErrors.password}
                    required
                  />
                )}
                {formMode === 'create' && (
                  <Input
                    label="Confirm Password *"
                    name="createConfirmPassword"
                    type="password"
                    value={form.confirmPassword}
                    onChange={(e) => updateField('confirmPassword', e.target.value)}
                    onInvalid={handleConfirmPasswordInvalid}
                    onInput={handleConfirmPasswordInput}
                    error={fieldErrors.confirmPassword}
                    required
                  />
                )}
                  </div>
                </div>
              )}

          </form>
        )}
      </Modal>

      <Modal
        open={showPasswordModal && Boolean(selectedPasswordUser)}
        onClose={closePasswordModal}
        title="Reset password"
        description={selectedPasswordUser ? `For ${selectedPasswordUser.name}` : ''}
        size="md"
        icon={(
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        )}
        footer={(
          <>
            <Button type="button" variant="secondary" onClick={closePasswordModal} disabled={passwordLoading}>
              Cancel
            </Button>
            <Button type="submit" form="admin-password-form" variant="primary" loading={passwordLoading} disabled={passwordLoading}>
              Update password
            </Button>
          </>
        )}
      >
        {showPasswordModal && selectedPasswordUser && (
          <form id="admin-password-form" onSubmit={handlePasswordUpdate} className="py-5 sm:py-6 space-y-4">
              {passwordError && (
                <AlertMessage
                  message={passwordError}
                  type="error"
                  onClose={() => setPasswordError('')}
                />
              )}
              <Input
                label="New Password *"
                type="password"
                value={passwordForm.password}
                onChange={(e) => setPasswordForm((prev) => ({ ...prev, password: e.target.value }))}
                minLength={8}
                maxLength={100}
                pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+=!]).{8,100}$"
                title="Password must contain uppercase, lowercase, number and special character"
                onInvalid={handlePasswordInvalid}
                onInput={handlePasswordInput}
                required
              />
              <Input
                label="Confirm Password *"
                name="resetConfirmPassword"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                onInvalid={handleConfirmPasswordInvalid}
                onInput={handleConfirmPasswordInput}
                required
              />
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmStatusUser)}
        title={`${confirmStatusUser?.active ? 'Deactivate' : 'Activate'} User`}
        message={confirmStatusUser ? `Do you want to ${confirmStatusUser.active ? 'deactivate' : 'activate'} "${confirmStatusUser.name}"?` : ''}
        confirmText={confirmStatusUser?.active ? 'Deactivate' : 'Activate'}
        cancelText="Cancel"
        onConfirm={() => {
          if (confirmStatusUser) {
            toggleUserStatus(confirmStatusUser);
          }
        }}
        onCancel={() => setConfirmStatusUser(null)}
        loading={Boolean(confirmStatusUser && statusLoadingId === confirmStatusUser.id)}
        variant={confirmStatusUser?.active ? 'danger' : 'primary'}
      />
    </div>
  );
}

export default AdminUsers;
